import type { SpectrumConfig } from './types/config.js';
import type { ChainSlug } from './types/chains.js';
import type { RequestOptions } from './types/client.js';
import type { JsonRpcRequest } from '@spectrum-nodes/api-spec/models';
import type { JsonRpcResponse } from './types/index.js';
import { MAX_JSON_RPC_BATCH_SIZE } from '@spectrum-nodes/api-spec/constants';
import { HttpClient } from './client.js';
import { CoreNamespace } from './namespaces/core.js';
import { TokensNamespace } from './namespaces/tokens.js';
import { YieldsNamespace } from './namespaces/yields.js';
import { UtilsNamespace } from './namespaces/utils.js';
import { PricesNamespace } from './namespaces/prices.js';
import { DefiNamespace } from './namespaces/defi.js';
import { NftsNamespace } from './namespaces/nfts.js';
import { EnsNamespace } from './namespaces/ens.js';
import { RegistryNamespace } from './namespaces/registry.js';
import { RpcNamespace } from './namespaces/rpc.js';
import { ContractsNamespace } from './namespaces/contracts.js';
import { SolanaNamespace } from './namespaces/solana.js';
import { CosmosNamespace } from './namespaces/cosmos.js';
import { DataNamespace } from './namespaces/data.js';
import { MarketplaceNamespace } from './namespaces/marketplace.js';
import { NetworkCalendarNamespace } from './namespaces/network-calendar.js';
import { ChainRef } from './internals/chain-ref.js';
import { resolveConfig } from './internals/config.js';
import { ValidationError } from './types/errors.js';

/**
 * Top-level entry point for the Spectrum SDK.
 *
 * Holds a shared {@link HttpClient} + {@link ChainRef} and exposes typed
 * namespaces (`core`, `tokens`, `defi`, `cosmos`, `marketplace`, `networkCalendar`, …) plus low-level
 * escape hatches ({@link Spectrum.jsonRpc}, `rpc.*`).
 *
 * @example
 * const sdk = new Spectrum('https://spectrum-01.simplystaking.xyz/MY_KEY/');
 * const eth = await sdk.tokens.getBalance('ethereum', '0xd8dA...');
 */
export class Spectrum {
  readonly core: CoreNamespace;
  readonly tokens: TokensNamespace;
  readonly yields: YieldsNamespace;
  readonly utils: UtilsNamespace;
  readonly prices: PricesNamespace;
  readonly defi: DefiNamespace;
  readonly nfts: NftsNamespace;
  readonly ens: EnsNamespace;
  readonly registry: RegistryNamespace;
  readonly rpc: RpcNamespace;
  readonly contracts: ContractsNamespace;
  readonly solana: SolanaNamespace;
  readonly cosmos: CosmosNamespace;
  readonly data: DataNamespace;
  readonly marketplace: MarketplaceNamespace;
  readonly networkCalendar: NetworkCalendarNamespace;

  private readonly client: HttpClient;
  private readonly chainRef: ChainRef;

  /**
   * Build a new SDK instance.
   *
   * Accepts either a {@link SpectrumConfig} object or the bare api URL string
   * as a shortcut (`new Spectrum(api)` ≡ `new Spectrum({ api })`).
   *
   * Throws if `api` is missing or isn't an http(s) URL.
   */
  constructor(config: SpectrumConfig | string) {
    const cfg: SpectrumConfig = typeof config === 'string' ? { api: config } : config;
    const resolvedConfig = resolveConfig(cfg);
    this.client = new HttpClient(resolvedConfig);
    this.chainRef = new ChainRef(resolvedConfig.defaultChain);

    this.core = new CoreNamespace(this.client, this.chainRef);
    this.tokens = new TokensNamespace(this.client, this.chainRef);
    this.yields = new YieldsNamespace(this.client);
    this.utils = new UtilsNamespace(this.client);
    this.prices = new PricesNamespace(this.client);
    this.defi = new DefiNamespace(this.client, this.chainRef);
    this.nfts = new NftsNamespace(this.client, this.chainRef);
    this.ens = new EnsNamespace(this.client);
    this.registry = new RegistryNamespace(this.client);
    this.rpc = new RpcNamespace(this.client, this.chainRef);
    this.contracts = new ContractsNamespace(this.client, this.chainRef);
    this.solana = new SolanaNamespace(this.client);
    this.cosmos = new CosmosNamespace(this.client, this.chainRef);
    this.data = new DataNamespace(this.client, this.chainRef);
    this.marketplace = new MarketplaceNamespace(this.client);
    this.networkCalendar = new NetworkCalendarNamespace(this.client);
  }

  /**
   * Send a raw JSON-RPC request (or batch) to Spectrum's `POST /v1` endpoint.
   *
   * Escape hatch for cases the typed namespaces don't cover — e.g. calling a
   * server method the SDK doesn't wrap yet. Batches are capped at
   * `MAX_JSON_RPC_BATCH_SIZE` (50); larger batches throw {@link ValidationError}.
   */
  async jsonRpc(
    request: JsonRpcRequest | JsonRpcRequest[],
    options?: RequestOptions,
  ): Promise<JsonRpcResponse | JsonRpcResponse[]> {
    if (Array.isArray(request) && request.length > MAX_JSON_RPC_BATCH_SIZE) {
      throw new ValidationError(
        'request',
        `Batch size exceeds maximum of ${MAX_JSON_RPC_BATCH_SIZE} requests`,
      );
    }

    return this.client.post<JsonRpcResponse | JsonRpcResponse[]>(request, options);
  }

  /**
   * Set the default chain used by methods called with `chain: undefined`.
   * Returns `this` so calls can be chained, e.g. `new Spectrum(cfg).setChain('base')`.
   */
  setChain(chain: ChainSlug): this {
    this.chainRef.current = chain;
    return this;
  }

  /** Current default chain, or `undefined` if never configured. */
  getChain(): ChainSlug | undefined {
    return this.chainRef.current;
  }

  /** Drop every cached response. Use after writes that invalidate read state. */
  clearCache(): void {
    this.client.clearCache();
  }
}
